import React, {useState} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  ScrollView,
  StyleSheet,
} from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import Bold from '../../typography/BoldText';
import Regular from '../../typography/RegularText';
import Medium from '../../typography/MediumText';
import colors from '../../util/colors';

const CalendarPicker = ({visible, onClose, onDateSelect}) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const isDateInRange = (date) => {
    const startDate = new Date('2025-07-12');
    const endDate = new Date('2025-07-31');
    return date >= startDate && date <= endDate;
  };

  const hasDevotion = (date) => {
    const dateString = formatDate(date);
    // Simple date range check - devotions are available from 2025-07-12 to 2025-07-31
    const devotionStart = new Date('2025-07-12');
    const devotionEnd = new Date('2025-07-31');
    const checkDate = new Date(dateString);
    return checkDate >= devotionStart && checkDate <= devotionEnd;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedMonth);
    const firstDay = getFirstDayOfMonth(selectedMonth);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<View key={`empty-${i}`} style={styles.calendarDay} />);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day);
      const dateString = formatDate(date);
      const isInRange = isDateInRange(date);
      const hasDevotionForDate = hasDevotion(date);
      const isSelected = selectedDate && formatDate(selectedDate) === dateString;

      days.push(
        <TouchableOpacity
          key={day}
          style={[
            styles.calendarDay,
            isInRange && hasDevotionForDate && styles.availableDay,
            isSelected && styles.selectedDay,
          ]}
          onPress={() => {
            if (isInRange && hasDevotionForDate) {
              setSelectedDate(date);
            }
          }}
          disabled={!isInRange || !hasDevotionForDate}
        >
          <Text style={[
            styles.dayText,
            isInRange && hasDevotionForDate && styles.availableDayText,
            isSelected && styles.selectedDayText,
          ]}>
            {day}
          </Text>
          {hasDevotionForDate && (
            <View style={styles.devotionDot} />
          )}
        </TouchableOpacity>
      );
    }

    return days;
  };

  const handleDateSelect = () => {
    if (selectedDate) {
      onDateSelect(formatDate(selectedDate));
      onClose();
    }
  };

  const changeMonth = (direction) => {
    const newMonth = new Date(selectedMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setSelectedMonth(newMonth);
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          {/* Header */}
          <View style={styles.modalHeader}>
            <Bold style={styles.modalTitle}>Select Date</Bold>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Icon name="close" size={24} color="#6C4D2A" />
            </TouchableOpacity>
          </View>

          {/* Month Navigation */}
          <View style={styles.monthNavigation}>
            <TouchableOpacity onPress={() => changeMonth(-1)} style={styles.navButton}>
              <Icon name="chevron-back" size={24} color="#6C4D2A" />
            </TouchableOpacity>
            <Bold style={styles.monthText}>
              {months[selectedMonth.getMonth()]} {selectedMonth.getFullYear()}
            </Bold>
            <TouchableOpacity onPress={() => changeMonth(1)} style={styles.navButton}>
              <Icon name="chevron-forward" size={24} color="#6C4D2A" />
            </TouchableOpacity>
          </View>

          {/* Calendar */}
          <View style={styles.calendarContainer}>
            {/* Day headers */}
            <View style={styles.dayHeaders}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <Text key={day} style={styles.dayHeader}>{day}</Text>
              ))}
            </View>

            {/* Calendar grid */}
            <View style={styles.calendarGrid}>
              {renderCalendar()}
            </View>
          </View>

          {/* Legend */}
          <View style={styles.legend}>
            <View style={styles.legendItem}>
              <View style={styles.legendDot} />
              <Regular style={styles.legendText}>Devotion available</Regular>
            </View>
          </View>

          {/* Action Buttons */}
          <View style={styles.actionButtons}>
            <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
              <Medium style={styles.cancelButtonText}>Cancel</Medium>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.selectButton, !selectedDate && styles.disabledButton]} 
              onPress={handleDateSelect}
              disabled={!selectedDate}
            >
              <Bold style={styles.selectButtonText}>Select Date</Bold>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: colors.antiqueWhite,
    borderRadius: 20,
    padding: 20,
    width: '90%',
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    color: colors.taupe,
  },
  closeButton: {
    padding: 8,
  },

  monthNavigation: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  navButton: {
    padding: 10,
    borderRadius: 8,
    backgroundColor: colors.ecru,
  },

  monthText: {
    fontSize: 18,
    color: colors.taupe,
  },
  calendarContainer: {
    marginBottom: 20,
  },
  dayHeaders: {
    flexDirection: 'row',
    marginBottom: 10,
  },
  dayHeader: {
    flex: 1,
    textAlign: 'center',
    fontSize: 14,
    color: colors.lion,
    fontWeight: 'bold',
  },
  calendarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  calendarDay: {
    width: '14.28%',
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',
    margin: 1,
  },
  availableDay: {
    backgroundColor: colors.background,
    borderRadius: 8,
  },
  selectedDay: {
    backgroundColor: colors.kobicha,
    borderRadius: 8,
  },
  dayText: {
    fontSize: 14,
    color: colors.lion,
  },
  availableDayText: {
    color: colors.taupe,
  },
  selectedDayText: {
    color: colors.antiqueWhite,
  },
  devotionDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.kobicha,
    position: 'absolute',
    bottom: 4,
  },
  legend: {
    marginBottom: 20,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.kobicha,
    marginRight: 8,
  },
  legendText: {
    fontSize: 14,
    color: colors.lion,
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 15,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.kobicha,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: colors.kobicha,
  },
  selectButton: {
    flex: 1,
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderRadius: 10,
    backgroundColor: colors.kobicha,
    alignItems: 'center',
  },
  disabledButton: {
    backgroundColor: colors.lion,
  },
  selectButtonText: {
    color: colors.antiqueWhite,
  },
});

export default CalendarPicker; 